import React from "react";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import Link from "@qlik/qlik/src/components/Link";
import Media from "@qlik/qlik/src/components/Media";
import { getTransformedId } from "@qlik/qlik/src/lib/util/format";
import { internalUrl } from "@qlik/qlik/src/lib/util/helpers";
import { componentRegistry } from "@qlik/qlik/src/components/registry";
import Cta from "@qlik/qlik/src/components/Cta";
import SingleIconCard from "@qlik/qlik/src/components/Card/Single-Icon-Card";
import Markdown from "@qlik/qlik/src/components/Markdown";
import Tooltip from "@qlik/qlik/src/components/Tooltip/Tooltip";
import { BLOG_PATHNAME } from "@qlik/qlik/src/lib/constants/blog-constants";
import { RESOURCE_PATHNAME } from "@qlik/qlik/src/lib/constants/resource-constants";
import { NEWS_PATHNAME } from "@qlik/qlik/src/lib/constants/news-constants";
import { TYPE_BLOG, TYPE_RESOURCE, TYPE_NEWS } from "@qlik/qlik/src/lib/constants/contentful-types";
import { TYPE_MICRO_ASSET } from "@qlik/qlik/gatsbyNode/constants";
import { normalizePath } from "@qlik/utils/normalizePathOrUrl";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

/**
 * renderLinkedComponents renders linked components based on the provided Contentful rich text node @data
 * If the data object is falsy, it uses an empty object
 * The function extracts the __typename, frontEndComponent, and id from the data, and uses these to get the corresponding component from the componentRegistry
 * If no component is found in the registry, it uses the defaultPlaceholder component
 * It then returns the rendered component with the provided data and id
 */
const renderLinkedComponents = (data) => {
    const { __typename, frontEndComponent, id, ...rest } = data ?? {};
    const componentKey = `${__typename}_${frontEndComponent}`;
    const Component = componentRegistry[componentKey] ?? componentRegistry.defaultPlaceholder;
    return <Component data={{ __typename, frontEndComponent, id, ...rest }} key={id} />;
};

/**
 * renderElementWithId renders an element with a specific tag and assigns a transformed ID to it
 * It takes an array of @children elements and a @tag name as input
 * The function creates a dynamic tag based on the provided tag name, generates a transformed ID from the first child,
 * and returns the rendered element with the transformed ID
 */
const renderElementWithId = (children, tag) => {
    const DynamicTag = `${tag}`;
    const id = getTransformedId(children[0]);
    return (
        <DynamicTag className="scroll-mt-35" id={id}>
            {children}
        </DynamicTag>
    );
};

/**
 * renderElementWithWrapper wraps the given @children elements inside a div and the specified @tag
 * The function creates a dynamic tag based on the provided tag name or array of tag names,
 * and returns a div with the children wrapped inside the dynamic tag, or tags.
 */
const renderElementWithWrapper = (children, tag) => {
    let TagEl,
        TagInner = "";
    // Account for when tag is an array of two tags (eg: table, tbody)
    if (Array.isArray(tag)) {
        TagEl = tag[0];
        TagInner = tag[1];
    } else {
        TagEl = tag;
        TagInner = React.fragment;
    }
    return (
        <div className={`${TagEl}-wrapper`}>
            <TagEl>
                <TagInner>{children}</TagInner>
            </TagEl>
        </div>
    );
};

/**
 * embedMapping creates a mapping of component types to their corresponding React components
 * It takes a @data object and a boolean indicating whether the component @isInline as input
 * The function returns an object mapping each component type to a React component with the provided data and inline status
 */
const embedMapping = (data, isInline) => ({
    ContentfulMedia: <Media media={data} inline={isInline} />,
    ContentfulCta: <Cta data={data} />,
    ContentfulTooltip: <Tooltip data={data} />,
    ContentfulCard: <SingleIconCard data={data} />,
    ContentfulMicroMarkdown: <Markdown data={data} />,
});

/**
 * renderEmbeddedEntry renders an embedded entry
 * It takes a @node , a @fallback component, an @override function, and a boolean indicating whether the component @isInline as input
 * The function gets the target from the node data, creates a map using the embedMapping function, and gets the corresponding component from the map
 * If the component is not found, it uses the fallback component
 * It returns the rendered component
 */
/* eslint-disable default-param-last */
const renderEmbeddedEntry = (node, fallback = null, override, isInline = false) => {
    const target = node?.data?.target;
    const map = embedMapping(target, isInline);
    const Component = { ...map, ...(override ? override(target) : {}) }[target?.__typename];
    return Component || fallback;
};

/**
 * contentfulTypePathnameMap is an object that maps Contentful types to their corresponding pathnames
 * It contains properties for blog (TYPE_BLOG), resource (TYPE_RESOURCE), and news (TYPE_NEWS) types
 */
const contentfulTypePathnameMap = {
    [TYPE_BLOG]: BLOG_PATHNAME,
    [TYPE_RESOURCE]: RESOURCE_PATHNAME,
    [TYPE_NEWS]: NEWS_PATHNAME,
};

/**
 * Applies custom styles to a component based on the presence of custom marks (utility classes - bold, italic, underline, etc.).
 *
 * @param {React.Component} component - The component to apply styles to.
 * @param {Array} customMarks - An array of custom marks.
 * @return {React.Component} The component with applied styles, or the original component if no styles are applied.
 */
const applyCustomStyles = (component, customMarks) => {
    /* @TODO: Expand to include other utility classes in future */
    let classes = [];
    if (customMarks && customMarks?.[0]?.type === "bold") classes.push("font-bold");

    return classes.length ? <span className={classes}>{component}</span> : component;
};

/**
 * formatContentfulRichText defines custom rendering for Contentful rich text nodes
 * It takes an @override function as input and returns an object mapping Contentful node types to render functions
 * The render functions handle different types of nodes like headings, tables, embedded entries, hyperlinks, and entry hyperlinks
 * They use helper functions like renderElementWithId, renderElementWithWrapper, and renderEmbeddedEntry to render the nodes
 */
const formatContentfulRichText = (override) => ({
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            // Check if the paragraph contains an embedded entry of type ContentfulCard
            if (
                // Ensure that the node has at least two children, as embedded entries have "text" before and after them
                node.content.length > 1 &&
                (node.content[1]?.nodeType === "embedded-entry-inline" ||
                    node.content[1]?.nodeType === "embedded-entry-block") &&
                node.content[1]?.data?.target?.__typename === "ContentfulCard"
            ) {
                // Do not wrap in a paragraph tag, if trying to render a card (invalid HTML in most if not all cases)
                return <React.Fragment>{children}</React.Fragment>;
            }
            // Otherwise, render the paragraph as usual
            return renderElementWithId(children, "p");
        },
        [BLOCKS.HEADING_1]: (node, children) => renderElementWithId(children, "h1"),
        [BLOCKS.HEADING_2]: (node, children) => renderElementWithId(children, "h2"),
        [BLOCKS.HEADING_3]: (node, children) => renderElementWithId(children, "h3"),
        [BLOCKS.HEADING_4]: (node, children) => renderElementWithId(children, "h4"),
        [BLOCKS.HEADING_5]: (node, children) => renderElementWithId(children, "h5"),
        [BLOCKS.HEADING_6]: (node, children) => renderElementWithId(children, "h6"),
        [BLOCKS.TABLE]: (node, children) => renderElementWithWrapper(children, ["table", "tbody"]),
        [BLOCKS.EMBEDDED_ENTRY]: (node) =>
            renderEmbeddedEntry(node, renderLinkedComponents(node.data.target), override),
        [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, null, override, true),
        [INLINES.HYPERLINK]: (node, children) => {
            // Create a constant containing the custom styles of this node (bold, italic, underline, etc.)
            const customMarks = null ?? node?.content?.[0]?.marks;

            const uri = node.data.uri;
            if (uri.startsWith("#")) {
                return <Link to={uri}>{children}</Link>;
            } else {
                return applyCustomStyles(<Link to={uri}>{children}</Link>, customMarks);
            }
        },
        [INLINES.ENTRY_HYPERLINK]: (node) => {
            // Create a constant containing the custom styles of this node (bold, italic, underline, etc.)
            const customMarks = null ?? node?.content?.[0]?.marks;

            if (node?.data?.target?.slug && node?.content?.[0]?.value) {
                const contentfulTypeName = node?.data?.target?.__typename;
                let slug = node.data.target.slug;
                if (
                    contentfulTypeName === TYPE_BLOG ||
                    contentfulTypeName === TYPE_RESOURCE ||
                    contentfulTypeName === TYPE_NEWS
                ) {
                    slug = normalizePath(`/${contentfulTypePathnameMap[contentfulTypeName]}/${slug}`);
                }

                return applyCustomStyles(
                    <Link to={slug}>{documentToReactComponents({ content: node.content })}</Link>,
                    customMarks
                );
            }

            if (node?.data?.target?.__typename === TYPE_MICRO_ASSET) {
                const internalLink = internalUrl(node?.data?.target, node?.data?.queryParams);
                return applyCustomStyles(
                    <Link newWindow={true} to={internalLink}>
                        {documentToReactComponents({ content: node.content })}
                    </Link>,
                    customMarks
                );
            }

            return null;
        },
    },
    renderText: (node) =>
        // eslint-disable-next-line react/no-array-index-key
        node.split("\n").flatMap((text, i, array) => (i === array.length - 1 ? text : [text, <br key={i} />])),
});

export default formatContentfulRichText;
